import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { fadeUp, stagger } from '@/lib/motion';

// Section wrapper with animations
export const Section = ({ children, className = '', id, dark = false, alt = false, ...rest }) => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });
  let bgClass = 'bg-white';
  if (dark) bgClass = 'bg-[#050E1F] text-white';
  else if (alt) bgClass = 'bg-[#F1F5F9]';
  
  return (
    <motion.section
      ref={ref}
      id={id}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={stagger}
      className={`py-12 sm:py-16 lg:py-20 ${bgClass} ${className}`}
      {...rest}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {children}
      </div>
    </motion.section>
  );
};

// Section title block
export const SectionTitle = ({ badge, title, subtitle, center = false, className = '' }) => (
  <motion.div variants={fadeUp} className={`mb-12 ${center ? 'text-center mx-auto max-w-2xl' : ''} ${className}`}>
    {badge && (
      <span className="inline-flex items-center gap-1.5 px-3 py-1 mb-4 text-xs font-semibold uppercase tracking-[0.18em] text-[#0EA5E9] bg-sky-50 border border-sky-100 rounded-full">
        {badge}
      </span>
    )}
    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight" style={{fontFamily:'Syne,sans-serif'}}>{title}</h2>
    {subtitle && <p className="mt-3 text-base sm:text-lg text-[#64748B] leading-relaxed">{subtitle}</p>}
  </motion.div>
);

// Animated card wrapper
export const AnimatedCard = ({ children, className = '', delay = 0 }) => (
  <motion.div variants={fadeUp} className={`rounded-2xl bg-white border border-slate-200 shadow-[0_10px_30px_rgba(2,6,23,0.08)] card-hover ${className}`}>
    {children}
  </motion.div>
);

// Authority Badge for page heroes
export const AuthorityBadge = ({ children, icon: Icon }) => (
  <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 text-xs font-semibold uppercase tracking-[0.18em] text-[#0EA5E9] bg-sky-500/10 border border-sky-500/20 rounded-full backdrop-blur-sm">
    {Icon && <Icon className="w-4 h-4" />}
    {children}
  </div>
);

// Page Hero component 
export const PageHero = ({ badge, badgeIcon, h1, subtitle, primaryCTA, secondaryCTA, dark = true, bgImage, children }) => {
  const BadgeIcon = badgeIcon;
  return (
    <div className={`relative overflow-hidden ${dark ? 'bg-[#050E1F] text-white' : 'bg-white'}`}>
      {bgImage && (
        <img src={bgImage} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ opacity: 0.35 }} />
      )}
      {dark && !bgImage && <div className="absolute inset-0 hero-gradient" />}
      {dark && <div className="absolute inset-0 noise-texture" />}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
        <div className="max-w-3xl">
          <AuthorityBadge icon={BadgeIcon}>{badge}</AuthorityBadge>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-semibold tracking-tight leading-tight mb-4" style={{fontFamily:'Syne,sans-serif'}}>{h1}</h1>
          <p className={`text-base sm:text-lg lg:text-xl leading-relaxed mb-8 ${dark ? 'text-slate-300' : 'text-[#64748B]'}`}>{subtitle}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            {primaryCTA}
            {secondaryCTA}
          </div>
        </div>
        {children}
      </div>
    </div>
  );
};
